/**
 * Hook for managing browser push notifications
 */

import { gql, useMutation } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

const REGISTER_PUSH_SUBSCRIPTION = gql`
    mutation RegisterPushSubscription(
        $endpoint: String!
        $p256dh: String!
        $auth: String!
        $userAgent: String
    ) {
        registerPushSubscription(
            endpoint: $endpoint
            p256dh: $p256dh
            auth: $auth
            userAgent: $userAgent
        ) {
            success
            message
        }
    }
`;

const UNREGISTER_PUSH_SUBSCRIPTION = gql`
    mutation UnregisterPushSubscription($endpoint: String!) {
        unregisterPushSubscription(endpoint: $endpoint) {
            success
            message
        }
    }
`;

// VAPID public key - this should match your backend VAPID key
const VAPID_PUBLIC_KEY = import.meta.env
    .VITE_VANDERHEIM_VAPID_PUBLIC_KEY as string;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export function usePushNotifications() {
    const [permission, setPermission] = useState<NotificationPermission>(
        "Notification" in window ? Notification.permission : "denied",
    );
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [registerPushSubscription] = useMutation(REGISTER_PUSH_SUBSCRIPTION);
    const [unregisterPushSubscription] = useMutation(
        UNREGISTER_PUSH_SUBSCRIPTION,
    );

    // Check if service worker and push notifications are supported
    const isSupported = useCallback(() => {
        return (
            "serviceWorker" in navigator &&
            "PushManager" in window &&
            "Notification" in window
        );
    }, []);

    // Register service worker
    const registerServiceWorker = useCallback(async () => {
        if (!isSupported()) {
            throw new Error("Push notifications are not supported");
        }

        try {
            const registration =
                await navigator.serviceWorker.register("/sw.js");
            console.debug("Service Worker registered:", registration);

            // Wait for the service worker to be active
            if (registration.installing) {
                await new Promise<void>((resolve) => {
                    registration.installing!.addEventListener(
                        "statechange",
                        (e) => {
                            if (
                                (e.target as ServiceWorker).state ===
                                "activated"
                            ) {
                                resolve();
                            }
                        },
                    );
                });
            } else if (registration.waiting) {
                // Service worker is waiting, activate it
                registration.waiting.postMessage({ type: "SKIP_WAITING" });
                await new Promise<void>((resolve) => {
                    navigator.serviceWorker.addEventListener(
                        "controllerchange",
                        () => resolve(),
                        { once: true },
                    );
                });
            }

            return registration;
        } catch (error) {
            console.error("Service Worker registration failed:", error);
            throw error;
        }
    }, [isSupported]);

    // Check subscription status
    const checkSubscription = useCallback(async () => {
        if (!isSupported()) return;

        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription =
                await registration.pushManager.getSubscription();
            setIsSubscribed(!!subscription);
            console.debug(
                "Subscription status:",
                subscription ? "subscribed" : "not subscribed",
            );
        } catch (error) {
            console.error("Failed to check subscription:", error);
        }
    }, [isSupported]);

    // Subscribe to push notifications
    const subscribe = useCallback(async () => {
        if (!isSupported()) {
            throw new Error("Push notifications are not supported");
        }

        setIsLoading(true);

        try {
            // Register service worker and wait for it to be ready
            await registerServiceWorker();

            // Wait for service worker to be active and ready
            const registration = await navigator.serviceWorker.ready;

            // Check for existing subscription and remove it first
            const existingSubscription =
                await registration.pushManager.getSubscription();
            if (existingSubscription) {
                console.debug(
                    "Removing existing subscription before creating new one",
                );
                await existingSubscription.unsubscribe();
                // Also notify backend to remove it
                try {
                    await unregisterPushSubscription({
                        variables: { endpoint: existingSubscription.endpoint },
                    });
                } catch (error) {
                    // Ignore error - subscription might already be gone
                    console.debug(
                        "Failed to unregister old subscription:",
                        error,
                    );
                }
            }

            // Request permission
            const permissionResult = await Notification.requestPermission();
            setPermission(permissionResult);

            if (permissionResult !== "granted") {
                throw new Error("Notification permission denied");
            }

            // Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(
                    VAPID_PUBLIC_KEY,
                ) as BufferSource,
            });

            // Send subscription to backend
            const subscriptionJSON = subscription.toJSON();
            await registerPushSubscription({
                variables: {
                    endpoint: subscription.endpoint,
                    p256dh: subscriptionJSON.keys?.p256dh || "",
                    auth: subscriptionJSON.keys?.auth || "",
                    userAgent: navigator.userAgent,
                },
            });

            setIsSubscribed(true);
            console.debug("Push subscription successful");
        } catch (error) {
            console.error("Push subscription failed:", error);
            // Check subscription status in case it partially succeeded
            await checkSubscription();
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [
        isSupported,
        registerServiceWorker,
        registerPushSubscription,
        unregisterPushSubscription,
        checkSubscription,
    ]);

    // Unsubscribe from push notifications
    const unsubscribe = useCallback(async () => {
        setIsLoading(true);

        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription =
                await registration.pushManager.getSubscription();

            if (subscription) {
                // Try to unregister from backend first (best effort)
                try {
                    await unregisterPushSubscription({
                        variables: { endpoint: subscription.endpoint },
                    });
                    console.debug("Backend unregister successful");
                } catch (error) {
                    // Ignore backend errors - subscription might not exist
                    console.debug(
                        "Backend unregister failed (expected):",
                        error,
                    );
                }

                // Always unsubscribe from browser
                try {
                    const unsubscribed = await subscription.unsubscribe();
                    console.debug("Browser unsubscribe result:", unsubscribed);
                } catch (error) {
                    console.error("Browser unsubscribe failed:", error);
                }

                setIsSubscribed(false);
                console.debug("Push unsubscription successful");
            } else {
                console.debug("No subscription found to unsubscribe");
                setIsSubscribed(false);
            }

            // Always refresh status after unsubscribe attempt
            await checkSubscription();
        } catch (error) {
            console.error("Push unsubscription failed (outer catch):", error);
            // Refresh subscription status
            await checkSubscription();
        } finally {
            setIsLoading(false);
        }
    }, [unregisterPushSubscription, checkSubscription]);

    // Check subscription status on mount
    useEffect(() => {
        checkSubscription();
    }, [checkSubscription]);

    return {
        permission,
        isSubscribed,
        isLoading,
        isSupported: isSupported(),
        subscribe,
        unsubscribe,
        checkSubscription,
    };
}
